import { lazy, Suspense, type ReactNode } from 'react';
import { BrowserRouter, Navigate, Route, Routes, useLocation } from 'react-router';
import { AppShell, RequireAuth } from './components/AppShell.tsx';
import { MorePage } from './components/ItNav.tsx';
import { useSession } from './lib/session.ts';
import { NotificationsPage, SettingsPage } from './pages/account.tsx';
import { ConfirmPage, SetupPage, SignInPage, VerifyPage } from './pages/auth.tsx';
import { ArticlePage, HelpPage } from './pages/help.tsx';
import { MyTicketsPage } from './pages/MyTicketsPage.tsx';
import { NewTicketPage } from './pages/NewTicketPage.tsx';
import { NotFoundPage } from './pages/NotFoundPage.tsx';
import { FeedbackPage, HomePage, StatusPage } from './pages/public.tsx';
import { TicketPage } from './pages/TicketPage.tsx';

// IT-only pages load on demand (see pages/it/workspace.ts).
const workspace = () => import('./pages/it/workspace.ts');
const QueuePage = lazy(() => workspace().then((m) => ({ default: m.QueuePage })));
const DashboardPage = lazy(() => workspace().then((m) => ({ default: m.DashboardPage })));
const FeedbackListPage = lazy(() => workspace().then((m) => ({ default: m.FeedbackListPage })));
const KbEditorPage = lazy(() => workspace().then((m) => ({ default: m.KbEditorPage })));
const UsersPage = lazy(() => workspace().then((m) => ({ default: m.UsersPage })));
const AuditPage = lazy(() => workspace().then((m) => ({ default: m.AuditPage })));
const SystemPage = lazy(() => workspace().then((m) => ({ default: m.SystemPage })));
const LaunchKitPage = lazy(() => workspace().then((m) => ({ default: m.LaunchKitPage })));
const OfficeHoursPage = lazy(() => workspace().then((m) => ({ default: m.OfficeHoursPage })));
const PosterPage = lazy(() => workspace().then((m) => ({ default: m.PosterPage })));
const TicketActions = lazy(() => workspace().then((m) => ({ default: m.TicketActions })));
const SavedReplyPicker = lazy(() => workspace().then((m) => ({ default: m.SavedReplyPicker })));
const SavedRepliesPage = lazy(() => workspace().then((m) => ({ default: m.SavedRepliesPage })));
const AnnouncementsPage = lazy(() => workspace().then((m) => ({ default: m.AnnouncementsPage })));
const EquipmentPage = lazy(() => workspace().then((m) => ({ default: m.EquipmentPage })));
const DevicePage = lazy(() => workspace().then((m) => ({ default: m.DevicePage })));
const MaintenancePage = lazy(() => workspace().then((m) => ({ default: m.MaintenancePage })));

/** IT users get the actions panel beside the conversation. */
function TicketRoute() {
  const { isIt } = useSession();
  return (
    <TicketPage
      sidePanel={
        isIt
          ? (detail) => (
              // Its own boundary: the conversation shows while the panel loads.
              <Suspense fallback={null}>
                <TicketActions detail={detail} />
              </Suspense>
            )
          : undefined
      }
      replyTools={
        isIt
          ? (detail, insert) => (
              <Suspense fallback={null}>
                <SavedReplyPicker detail={detail} onInsert={insert} />
              </Suspense>
            )
          : undefined
      }
    />
  );
}

/**
 * Addresses from the old helpdesk: bookmarks, and rating links in emails it
 * already sent (/feedback.html?token=…), keep working after the switch.
 */
function LegacyFeedbackLink() {
  const token = new URLSearchParams(useLocation().search).get('token');
  return <Navigate to={token ? { pathname: '/feedback', hash: `token=${token}` } : '/feedback'} replace />;
}
const LEGACY_REDIRECTS: Record<string, string> = {
  'index.html': '/',
  'status.html': '/status',
  'admin/login.html': '/sign-in?it=1',
  'admin/dashboard.html': '/dashboard',
  'admin/setup.html': '/setup',
};

const signedIn = (page: ReactNode, role?: 'it' | 'admin') => <RequireAuth role={role}>{page}</RequireAuth>;

export function App() {
  return (
    <BrowserRouter>
      <Routes>
        <Route element={<AppShell />}>
          {/* Public */}
          <Route index element={<HomePage />} />
          <Route path="new" element={<NewTicketPage />} />
          <Route path="status" element={<StatusPage />} />
          <Route path="feedback" element={<FeedbackPage />} />
          <Route path="help" element={<HelpPage />} />
          <Route path="help/:id" element={<ArticlePage />} />
          <Route path="sign-in" element={<SignInPage />} />
          <Route path="sign-in/verify" element={<VerifyPage />} />
          <Route path="confirm" element={<ConfirmPage />} />
          <Route path="setup" element={<SetupPage />} />

          {/* Signed in */}
          <Route path="tickets" element={signedIn(<MyTicketsPage />)} />
          <Route path="tickets/:number" element={signedIn(<TicketRoute />)} />
          <Route path="notifications" element={signedIn(<NotificationsPage />)} />
          <Route path="settings" element={signedIn(<SettingsPage />)} />

          {/* IT */}
          <Route path="queue" element={signedIn(<QueuePage />, 'it')} />
          <Route path="dashboard" element={signedIn(<DashboardPage />, 'it')} />
          <Route path="ratings" element={signedIn(<FeedbackListPage />, 'it')} />
          <Route path="help/new" element={signedIn(<KbEditorPage />, 'it')} />
          <Route path="help/:id/edit" element={signedIn(<KbEditorPage />, 'it')} />
          <Route path="replies" element={signedIn(<SavedRepliesPage />, 'it')} />
          <Route path="announcements" element={signedIn(<AnnouncementsPage />, 'it')} />
          <Route path="equipment" element={signedIn(<EquipmentPage />, 'it')} />
          <Route path="equipment/:id" element={signedIn(<DevicePage />, 'it')} />
          <Route path="maintenance" element={signedIn(<MaintenancePage />, 'it')} />
          <Route path="more" element={signedIn(<MorePage />, 'it')} />

          {/* Admin */}
          <Route path="admin/users" element={signedIn(<UsersPage />, 'admin')} />
          <Route path="admin/audit" element={signedIn(<AuditPage />, 'admin')} />
          <Route path="admin/system" element={signedIn(<SystemPage />, 'admin')} />
          <Route path="admin/launch" element={signedIn(<LaunchKitPage />, 'admin')} />
          <Route path="admin/office" element={signedIn(<OfficeHoursPage />, 'admin')} />

          <Route path="feedback.html" element={<LegacyFeedbackLink />} />
          {Object.entries(LEGACY_REDIRECTS).map(([from, to]) => (
            <Route key={from} path={from} element={<Navigate to={to} replace />} />
          ))}

          <Route path="*" element={<NotFoundPage />} />
        </Route>
        {/* Printable, without the app's header and menus. */}
        <Route
          path="poster"
          element={signedIn(
            <Suspense fallback={null}>
              <PosterPage />
            </Suspense>,
            'it',
          )}
        />
      </Routes>
    </BrowserRouter>
  );
}
